const LONGEST_ENTRY_NAME_FIRST = (entryName, otherEntryName) => otherEntryName.length - entryName.length;
const GLOSSARY_ENTRY_PLACEHOLDER_REGEX = new RegExp("(\{[\\d]+\})", "g");


/**
 * How does the glossary helper work?
 *
 * 1. Step: Sort glossary entries names by the longest name first
 * 2. Step: Replace each occurrence of a glossary entry by an identifiable placeholder in the given text
 *          Notice: The text now contains placeholder for each provided glossary entry and a mapping of
 *          placeholder -> replacement
 *
 * 3. Step: The text is split after each placeholder occurrence
 * 4. Step: Iterating over each text snippet checking whether it's a placeholder or not
 *
 * 4a. Step: If it is a placeholder, the placeholder gets replaced by the corresponding replacement that is
 * identified by the placeholder index -> the renderGlossaryEntry(label, entry) method gets invoked
 *
 * 4b. Step: If it is not a placeholder -> the renderNonGlossaryEntry(text) methods gets invoked
 *
 * This allows the glossary entries to be treated separately from the normal text.
 *
 * Example:
 * text: Obermaterial: 95% Viskose (nachhaltig), 5% Elasthan, Spitze: 90% Polyamid, 10% Elasthan
 * glossary entries incl. description: {"viskose": "...", "elasthan": "...", "polyamid": "...", "elasthan": "..."}
 *
 * 1. Step: ["elasthan", "polyamid", "viskose", "abs"]
 * 2. Step:
 *  text: Obermaterial: 95% {2} (nachhaltig), 5% {0}, Spitze: 90% {1}, 10% {0}
 *  replacements: {0: {name: "elasthan", target: "Elasthan}, 1: {name: "polyamid", target: "Polyamid"}, 2: {name: "viskose", target: "Viskose"}}
 *
 * 3. Step:
 *  splitted text: ['Obermaterial: 95% ', '{2}', ' (nachhaltig), 5% ', '{0}', ', Spitze: 90% ', '{1}', ', 10% ', '{0}', '']
 *
 * 4a. Step:
 *  -> for the entries ['{2}', '{0}', '{1}', '{0}'] the renderGlossaryEntry(label, entry) method
 *  gets called with the resolved replacements
 *
 * 4b. Step:
 *  -> for the entries ['Obermaterial: 95% ', ' (nachhaltig), 5% ', ', Spitze: 90% ', ', 10% ', ''] the renderNonGlossaryEntry(text)
 *  method gets called with the entry itself
 *
 */

function glossify(text, renderGlossaryEntry, renderNonGlossaryEntry) {
    if(this.entryNames.length === 0) {
        return renderNonGlossaryEntry(text, 0);
    }

    let replacements = {};
    const onCollectGlossaryEntryReplacement = (index, replacement) => replacements[index] = replacement;
    return this.entryNames.reduce(markGlossaryEntries(onCollectGlossaryEntryReplacement), text)
        .split(GLOSSARY_ENTRY_PLACEHOLDER_REGEX)
        .map((textSnippet, index) => {
            if(textSnippet.match(GLOSSARY_ENTRY_PLACEHOLDER_REGEX)) {
                const index = textSnippet.substring(1, textSnippet.length - 1);
                const replacement = replacements[index];
                return renderGlossaryEntry(replacement.target, this.entries[replacement.name]);
            }
            return renderNonGlossaryEntry(textSnippet, index);
        });
}

function create(glossary) {
    //the glossary entry labels have to be sorted in order
    //to render the longest variant of an entry first
    const entryNames = Object.keys(glossary.entries)
        .sort(LONGEST_ENTRY_NAME_FIRST);

    return {
        entries: glossary.entries,
        entryNames,
        glossify
    };
}

function markGlossaryEntries(glossaryEntryReplacementCollector) {
    //reduce function
    return (text, entryName, currentIndex) => {
      const regex = new RegExp(`(\\W|\\s|&nbsp;|^)(${entryName})(\\W|&nbsp;|$)`, 'gi');
      //replacer function (all -> the whole text, start, target, end -> the regex groups in order
        return text.replace(regex, (all, start, target, end) => {
            if(!target || target.length === 0) {
                return all;
            }
            glossaryEntryReplacementCollector?.(currentIndex, {name: entryName, target});
            //replacing the glossary entry by using a placeholder
            return `${start}{${currentIndex}}${end}`;
        });
    };
}

export default {
    create
}

